import { useEffect } from 'react';
import Highlight from '@tiptap/extension-highlight';
import { CharacterCount, type CharacterCountOptions } from '@tiptap/extensions';
import { type Node as ProseMirrorNode } from '@tiptap/pm/model';
import { Plugin, type Transaction } from '@tiptap/pm/state';
import { type Editor, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { logger } from '@zep/core-logger';
import { MathInline } from '@/shared/features/rich-text';
import { useEditMode } from '../../focus/useIsEditMode';
import { openEquationEditorModal } from '../EquationEditorModal';
import { type EquationCharacterBudget } from '../utils/getEquationBudgetState';

// 수식을 센 글자 수만큼 채우는 자리표시 글자. `textCounter` 가 지우는 줄바꿈만 아니면 된다.
const FORMULA_FILLER = 'x';

// 폼 값을 에디터로 옮기는 트랜잭션 표시. 글자수 한도 검사를 건너뛴다.
const FORM_SYNC_META = 'quizFormSync';

/**
 * 기본 플러그인은 넘친 만큼 커서 앞 문서 위치(`head − over` ~ `head`)를 지운다. 그 범위에 수식이
 * 끼면 위치 1칸인 수식이 여러 자로 세여 센 것보다 많이 지우고, 붙여넣기 앞 기존 글자까지 지운다.
 */
const trimRangeHasFormula = (transaction: Transaction, over: number) => {
  const head = transaction.selection.$head.pos;
  const from = head - over;
  if (from < 0) return true;
  let hasFormula = false;
  transaction.doc.nodesBetween(from, head, node => {
    if (node.type.name === MathInline.name) hasFormula = true;
    return !hasFormula;
  });
  return hasFormula;
};

/**
 * 입력 중 글자수 제한. 친 글자는 공백까지 그대로 세고, 수식은 `formulaCounter`(저장 검증
 * `countVisibleQuizText` 와 같은 함수)로 센다. 저장 검증은 공백을 접어 세므로 에디터가 같거나 더
 * 엄격하다 — 공백까지 접으면 센 글자 수와 문서 위치가 어긋나 붙여넣기 잘라내기가 깨진다.
 */
const QuizCharacterCount = CharacterCount.extend<CharacterCountOptions & {
  /**
   * 문단 사이를 셀 글자. 기본 카운터는 문단 사이를 세지 않는데, 저장값(`extractBareText`)은
   * 문단 사이에 줄바꿈을 넣어 1자로 센다. 짝맞추기처럼 줄바꿈을 그대로 저장하는 칸은 `'\n'` 을
   * 넘겨 에디터와 저장 검증이 같은 수를 내게 한다.
   */
  blockSeparator?: string;
  /** 수식 하나의 글자 수. 없으면 수식을 1자로 센다. */
  formulaCounter?: (latex: string) => number;
}>({
  addOptions() {
    return {
      ...(this.parent?.() as CharacterCountOptions),
      blockSeparator: undefined,
      formulaCounter: undefined
    };
  },
  onBeforeCreate(event) {
    this.parent?.(event);
    const {
      blockSeparator,
      formulaCounter,
      textCounter
    } = this.options;
    if (blockSeparator === undefined && !formulaCounter) return;
    const leafText = (leaf: ProseMirrorNode) => formulaCounter && leaf.type.name === MathInline.name ? FORMULA_FILLER.repeat(formulaCounter(leaf.attrs.latex ?? '')) : ' ';
    this.storage.characters = options => {
      const node = options?.node ?? this.editor.state.doc;
      return textCounter(node.textBetween(0, node.content.size, blockSeparator, leafText));
    };
  },
  // 기본 플러그인은 처음 열 때 한도를 넘은 문서의 앞부분을 지운다(appendTransaction). 저장된
  // 내용이 에디터를 여는 것만으로 사라지면 안 되므로 그 동작은 빼고, 입력 차단과 붙여넣기
  // 잘라내기(filterTransaction)만 남긴다. 한도를 넘은 문서는 줄이는 편집만 허용된다.
  // ⚠️ 공식 옵션이 없어 CharacterCount 의 플러그인 구성(플러그인 1개, 그 spec 의
  // appendTransaction·filterTransaction, storage.characters 를 filterTransaction 이 호출)에
  // 기댄다. @tiptap/extensions 를 올릴 때 한도를 넘은 문서를 열어도 잘리지 않는지, 한도에서
  // 타이핑이 막히고 붙여넣기가 잘리는지 확인한다.
  addProseMirrorPlugins() {
    const {
      formulaCounter,
      limit
    } = this.options;
    return (this.parent?.() ?? []).map(plugin => {
      const filterTransaction = plugin.spec.filterTransaction;
      return new Plugin({
        ...plugin.spec,
        appendTransaction: undefined,
        filterTransaction: (transaction, state) => {
          if (transaction.getMeta(FORM_SYNC_META)) return true;
          // 잘라내기는 한도 안 문서에 붙여넣다 넘칠 때만 일어난다.
          if (formulaCounter && limit && transaction.getMeta('paste')) {
            const oldSize = this.storage.characters({
              node: state.doc
            });
            const over = this.storage.characters({
              node: transaction.doc
            }) - limit;
            if (oldSize <= limit && over > 0 && trimRangeHasFormula(transaction, over)) {
              return false;
            }
          }
          return filterTransaction?.call(plugin, transaction, state) ?? true;
        }
      });
    });
  }
});

/**
 * 문서의 `from`~`to`(선택한 글자, 고치는 수식)를 수식으로 바꿀 때 수식 편집 창에 넘길 글자 수 예산.
 * 그 범위는 지워지므로 나머지 글자에서 빼고, 입력칸과 같은 카운터로 센다.
 */
export const getFormulaReplacementBudget = (editor: Editor, {
  from,
  to
}: {
  from: number;
  to: number;
}, limit: number): EquationCharacterBudget | undefined => {
  const characterCount = editor.storage.characterCount;
  if (!characterCount) return undefined;
  const total = characterCount.characters();
  const replaced = characterCount.characters({
    node: editor.state.doc.cut(from, to)
  });
  return {
    limit,
    otherCount: total - replaced,
    initialTotal: total
  };
};

/**
 * 퀴즈 생성 시 싱글톤 텍스트 에디터를 제공하는 hook
 */
export const useBuildTextEditor = ({
  content,
  onUpdate,
  characterLimit = 300,
  characterCounter,
  blockSeparator,
  formulaMaxLength,
  formulaCounter
}: {
  content?: string;
  onUpdate?: (content: string) => void;
  characterLimit?: number;
  characterCounter?: (text: string) => number;
  /** 문단 사이를 셀 글자. `QuizCharacterCount` 의 같은 옵션 참고. */
  blockSeparator?: string;
  /** 수식 하나의 LaTeX 원문 상한. */
  formulaMaxLength?: number;
  /** 수식 하나의 글자 수. `QuizCharacterCount` 의 같은 옵션 참고. */
  formulaCounter?: (latex: string) => number;
}) => {
  const {
    onEditModeChange
  } = useEditMode();
  const editor = useEditor({
    extensions: [StarterKit, QuizCharacterCount.configure({
      limit: characterLimit,
      mode: 'textSize',
      ...(characterCounter && {
        textCounter: characterCounter
      }),
      blockSeparator,
      formulaCounter
    }), Highlight.configure({
      multicolor: true
    }),
    // Custom MathInline extension with onClick handler
    MathInline.configure({
      onClick: async (node: ProseMirrorNode, pos: number, editor: Editor) => {
        const initialValue: string = node?.attrs?.latex ?? '';
        const latex = await openEquationEditorModal({
          initialValue,
          maxLength: formulaMaxLength,
          characterBudget: formulaCounter ? getFormulaReplacementBudget(editor, {
            from: pos,
            to: pos + node.nodeSize
          }, characterLimit) : undefined
        });
        if (latex) {
          editor.chain().setNodeSelection(pos).updateAttributes('mathInline', {
            latex
          }).focus().run();
          editor.commands.focus('end');
        }
        logger.track('build_addformula', {
          location: 'question'
        });
      }
    })],
    immediatelyRender: false,
    content,
    onUpdate: ({
      editor
    }) => {
      const html = editor.getHTML();
      onUpdate?.(editor.isEmpty ? '' : html);
    },
    parseOptions: {
      preserveWhitespace: 'full'
    },
    editorProps: {
      attributes: {
        class: 'w-full border-none focus-within:outline-none focus:outline-none focus-visible:outline-none',
        'data-no-focus-visible': 'true'
      }
    }
  });

  // content가 변경되었을 때 에디터 내용을 업데이트
  useEffect(() => {
    if (editor && content !== undefined) {
      const currentContent = editor.getHTML();
      if (currentContent !== content) {
        // 폼 값을 에디터로 옮기는 동기화라 onUpdate 로 폼에 되쓰지 않는다(TipTap 3 기본값은 emit).
        // 되쓰면 index 경로가 바뀌는 커밋에서 잠깐 보인 빈 값이 다른 보기를 지운다.
        // 글자수 한도도 걸지 않는다 — 폼이 원본이라, 거부되면 화면만 옛 내용으로 남는다.
        editor.chain().setMeta(FORM_SYNC_META, true).setContent(content, {
          emitUpdate: false
        }).run();
      }
    }
  }, [content, editor]);
  useEffect(() => {
    if (editor) {
      editor.on('focus', () => {
        onEditModeChange(true);
      });
    }
    // return () => {
    //   editor?.off('focus');
    //   editor?.off('blur');
    // };
  }, [editor, onEditModeChange]);
  return {
    editor
  };
};